"use client";

// Booster d'XP dans la barre du haut, donc partout sur le site connecté.
//
// Pourquoi ici : un booster multiplie TOUT l'XP gagné sur Believemy (sessions
// de formation, quiz, Reps, leagues), il se gagne dans les coffres et s'achète
// en boutique... mais il ne se voyait et ne s'activait que dans le rail de
// /reps. Résultat : des apprenants avec du stock qui ne savaient ni à quoi il
// servait, ni où l'utiliser. La pastille se pose donc là où l'XP se gagne,
// c'est-à-dire sur toutes les pages, et l'activation se fait sur place — sans
// détour par /reps, qui laissait croire que le booster était réservé aux Reps.
//
// Elle n'apparaît QUE si on a un booster (actif ou en stock) : une quatrième
// icône permanente à côté de la série, des fragments et des clés serait du
// bruit pour la majorité, qui n'en a pas. Corollaire assumé : cet écran
// n'explique rien à qui n'en a jamais eu — c'est le rôle de la boutique.
//
// Elle vit HORS de la capsule Série/Fragments/Clés, pour deux raisons : un
// compte à rebours n'est pas une monnaie (il mérite sa couleur), et une
// capsule dont un membre apparaît et disparaît ferait sauter les arrondis de
// ses voisins à chaque activation.

import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { toast } from "react-toastify";
import { Link } from "@/i18n/navigation";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import BottomSheetModal from "@/components/ui/BottomSheetModal/BottomSheetModal";
import ResourcePopup from "../ResourcePopup/ResourcePopup";
import useXpBoosters, {
    EXPIRY_WARNING_DAYS,
    formatCountdown,
    formatCountdownShort,
    groupBoosters,
} from "@/hooks/useXpBoosters";

// Au-delà, les lots se replient : le stock tient en un seul lot en pratique
// (toutes les sources donnent le même ×2 de 30 min), c'est un filet.
const MAX_VISIBLE_GROUPS = 3;

// Icône booster = l'illustration maison boost.svg (rouge/jaune, fiery),
// la même que la boutique et la carte du rail /reps.
function BoostIcon({ size = 16, className = "" }) {
    return (
        <Image
            src="/static/icons/boost.svg"
            width={Math.round(size * 0.62)}
            height={size}
            alt=""
            className={className}
        />
    );
}

/**
 * Le corps du panneau, identique au survol (ordinateur) et dans la feuille du
 * bas (téléphone) : une seule version du discours à maintenir.
 */
function BoostPanel({ active, groups, activatingId, onActivate }) {
    const t = useTranslations("layout");
    const visibleGroups = groups.slice(0, MAX_VISIBLE_GROUPS);
    const hiddenCount = groups.length - visibleGroups.length;
    const reserve = groups.reduce((total, g) => total + g.count, 0);

    return (
        <>
            {/* La phrase qui manquait : le booster ne sert pas qu'aux Reps. */}
            <p className="mb-0 mt-1 font-geistSans text-sm text-[#06050F]/60 dark:text-textSecondDark">
                {t("Boost_description")}
            </p>

            {active && (
                <div className="mt-3 flex items-center justify-between rounded-lg bg-gradient-to-r from-orange-500 to-red-500 px-3 py-2 text-white">
                    <span className="text-sm font-black tabular-nums">
                        ×{active.multiplier}
                    </span>
                    <span className="text-xs font-semibold tabular-nums">
                        {t("Boost_remaining", {
                            time: formatCountdown(active.activeUntil),
                        })}
                    </span>
                </div>
            )}

            {groups.length > 0 && (
                <>
                    {/* Un seul booster peut tourner à la fois : tant qu'il en
                        reste un actif, le stock est une réserve, pas une liste
                        de boutons désactivés qui invitent à cliquer. */}
                    {active ? (
                        <p className="mb-0 mt-3 text-sm text-gray-500 dark:text-grayDark-400">
                            {t("Boost_reserve", { count: reserve })}
                        </p>
                    ) : (
                        <ul className="mt-3 space-y-1.5">
                            {visibleGroups.map((g) => (
                                <li
                                    key={g.key}
                                    className="rounded-lg border border-gray-100 px-2.5 py-1.5 dark:border-white/10"
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="flex min-w-0 items-center gap-1.5 text-sm text-gray-700 dark:text-grayDark-200">
                                            <BoostIcon size={16} />
                                            <span className="font-semibold">
                                                ×{g.multiplier}
                                            </span>
                                            <span className="text-xs text-gray-400">
                                                {t("Boost_duration", {
                                                    minutes: g.durationMinutes,
                                                })}
                                            </span>
                                            {/* Le compte n'a de sens qu'à
                                                partir de deux, et reste nu : un
                                                "×50" à côté du "×2" du
                                                multiplicateur se lirait comme
                                                un réglage du booster. */}
                                            {g.count > 1 && (
                                                <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-bold tabular-nums text-gray-600 dark:bg-white/10 dark:text-grayDark-300">
                                                    {g.count}
                                                </span>
                                            )}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => onActivate(g.nextId)}
                                            disabled={activatingId === g.nextId}
                                            className="shrink-0 rounded-md bg-orange-500 px-2.5 py-1 text-xs font-semibold text-white transition-colors hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-40"
                                        >
                                            {activatingId === g.nextId
                                                ? t("Boost_activating")
                                                : t("Boost_activate")}
                                        </button>
                                    </div>

                                    {/* Péremption : on ne le dit qu'au moment
                                        où c'est actionnable, sinon c'est du
                                        bruit permanent. */}
                                    {g.expiringSoon > 0 && (
                                        <p className="mb-0 mt-1 text-xs font-semibold text-amber-600 dark:text-amber-400">
                                            {t("Boost_expiring", {
                                                count: g.expiringSoon,
                                                days: EXPIRY_WARNING_DAYS,
                                            })}
                                        </p>
                                    )}
                                </li>
                            ))}
                            {hiddenCount > 0 && (
                                <li className="text-xs text-gray-400">
                                    {t("Boost_more_groups", {
                                        count: hiddenCount,
                                    })}
                                </li>
                            )}
                        </ul>
                    )}
                </>
            )}
        </>
    );
}

export default function Boost({ session, showTipsOnTop }) {
    const t = useTranslations("layout");
    const enabled = !!(session && session.user);
    const { active, inventory, activate } = useXpBoosters(enabled);

    const [openPopup, setOpenPopup] = useState(false);
    const [openSheet, setOpenSheet] = useState(false);
    const [activatingId, setActivatingId] = useState(null);
    const [, setTick] = useState(0);

    // Tic chaque seconde pour le décompte, seulement quand un booster tourne :
    // sans booster actif, il n'y a rien à décompter.
    const hasActive = !!active;
    useEffect(() => {
        if (!hasActive) return undefined;
        const id = setInterval(() => setTick((n) => n + 1), 1000);
        return () => clearInterval(id);
    }, [hasActive]);

    // Le décompte arrivé à zéro ne se retire pas tout seul : sans nouvelle
    // lecture, la pastille afficherait "0s" jusqu'au prochain changement de
    // page. On redemande l'état une fois l'échéance passée.
    const activeUntil = active ? active.activeUntil : null;
    const expiredRef = useRef(null);
    useEffect(() => {
        if (!activeUntil) return undefined;
        const ms = new Date(activeUntil).getTime() - Date.now();
        if (ms <= 0) {
            // Une seule relecture par échéance, sinon un back qui renvoie
            // encore le booster (horloges décalées) déclencherait une boucle.
            if (expiredRef.current === activeUntil) return undefined;
            expiredRef.current = activeUntil;
            window.dispatchEvent(new Event("reps:boosters-changed"));
            return undefined;
        }
        const id = setTimeout(() => {
            window.dispatchEvent(new Event("reps:boosters-changed"));
        }, ms + 1000);
        return () => clearTimeout(id);
    }, [activeUntil]);

    const groups = groupBoosters(inventory);

    // Rien en main = rien à montrer. Tant que l'état est inconnu, `active` est
    // null et `groups` vide : on n'affiche donc jamais une pastille vide en
    // attendant la réponse.
    if (!active && groups.length === 0) return null;

    async function onActivate(id) {
        setActivatingId(id);
        try {
            const ok = await activate(id);
            if (!ok) {
                toast.error(t("Boost_one_active"));
                return;
            }
            toast.success(t("Boost_activated"), {
                icon: () => <BoostIcon size={22} />,
            });
            setOpenSheet(false);
        } catch (e) {
            toast.error(t("Boost_one_active"));
        } finally {
            setActivatingId(null);
        }
    }

    const stockCount = groups.reduce((total, g) => total + g.count, 0);

    // Actif : dégradé orange, l'état exceptionnel a le droit de se voir, avec
    // son décompte lisible sans ouvrir quoi que ce soit. En réserve seulement :
    // teinte ambre discrète, au niveau sonore des autres ressources.
    const pill = active ? (
        <>
            <BoostIcon size={16} />
            <span className="font-geistMono text-sm font-bold tabular-nums text-white">
                {formatCountdownShort(active.activeUntil)}
            </span>
        </>
    ) : (
        <>
            <BoostIcon size={16} />
            <span className="font-geistMono text-sm font-bold tabular-nums text-amber-600 dark:text-amber-400">
                {stockCount}
            </span>
        </>
    );

    const pillClass = active
        ? "bg-gradient-to-r from-orange-500 to-red-500 border-transparent"
        : "bg-amber-50 border-amber-200/70 dark:bg-amber-500/10 dark:border-amber-500/20";

    return (
        <>
            {/* Ordinateur : même geste que la série, les fragments et les
                clés — on survole, on lit, on agit. */}
            <div
                className="relative hidden md:block"
                onMouseOver={() => setOpenPopup(true)}
                onMouseOut={() => setOpenPopup(false)}
            >
                <div
                    className={`flex cursor-pointer items-center gap-1.5 rounded-full border px-2.5 py-1.5 duration-100 ${pillClass}`}
                >
                    {pill}
                </div>

                <ResourcePopup
                    open={openPopup}
                    showTipsOnTop={showTipsOnTop}
                    icon={<BoostIcon size={18} />}
                    title={t("Boost_title")}
                    badge={
                        active ? (
                            <div className="flex items-center gap-1 rounded-full bg-orange-50 px-2.5 py-1 dark:bg-orange-500/10">
                                <span className="text-xs font-semibold text-orange-600 dark:text-orange-400">
                                    {t("Boost_active_badge")}
                                </span>
                            </div>
                        ) : null
                    }
                    cta={
                        // La boutique n'est proposée que quand il ne reste
                        // rien à activer : avec du stock en réserve, inviter à
                        // en acheter un de plus est un contresens.
                        active && groups.length === 0
                            ? {
                                  href: "/fragments",
                                  label: t("Boost_get_more"),
                                  variant: "default",
                              }
                            : null
                    }
                >
                    <BoostPanel
                        active={active}
                        groups={groups}
                        activatingId={activatingId}
                        onActivate={onActivate}
                    />
                </ResourcePopup>
            </div>

            {/* Téléphone : la capsule des ressources est masquée sous md, et le
                survol n'existe pas. Un tap ouvre la feuille du bas maison. */}
            <button
                type="button"
                onClick={() => setOpenSheet(true)}
                aria-label={t("Boost_open")}
                className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1.5 duration-100 md:hidden ${pillClass}`}
            >
                {pill}
            </button>

            <BottomSheetModal
                isOpen={openSheet}
                onClose={() => setOpenSheet(false)}
                labelledBy="boost-sheet-title"
                // La barre du haut porte un backdrop-blur : sans portail, le
                // `position: fixed` de la feuille se recalerait sur elle.
                portal
            >
                <div className="p-5">
                    <div className="mb-1 flex items-center justify-between gap-3">
                        <div className="flex min-w-0 items-center gap-2">
                            <BoostIcon size={18} />
                            <span
                                id="boost-sheet-title"
                                className="truncate font-sourceSans text-lg font-bold text-[#06050F] dark:text-white"
                            >
                                {t("Boost_title")}
                            </span>
                        </div>
                        <button
                            type="button"
                            onClick={() => setOpenSheet(false)}
                            aria-label={t("Boost_close")}
                            className="-mr-1 grid h-8 w-8 shrink-0 place-items-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-white/10 dark:hover:text-grayDark-200"
                        >
                            <SolarIcon
                                name="close-circle-linear"
                                className="text-xl"
                            />
                        </button>
                    </div>

                    <BoostPanel
                        active={active}
                        groups={groups}
                        activatingId={activatingId}
                        onActivate={onActivate}
                    />

                    {active && groups.length === 0 && (
                        <Link
                            href="/fragments"
                            onClick={() => setOpenSheet(false)}
                            className="btn-3d lip-orange mt-4 flex items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-white transition-opacity hover:text-white hover:opacity-95"
                        >
                            {t("Boost_get_more")}
                        </Link>
                    )}
                </div>
            </BottomSheetModal>
        </>
    );
}
